import React, { useEffect, useState } from 'react';
import { View, Switch } from 'react-native';
import MyAppText from '../MyAppText';
import { translate } from '../../services/translate';
import { WindowInformation } from '../../services/window-information';
import FormInput from '../FormInput';
import { appAgentService } from '../../services/central-api/app-agent';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { isPlateInvalid } from '../helpers/plate';
import BasicModal from '../BasicModal';
import FilterDropdown from '../Filter/components/FilterDropdown';

interface InvolvedVehicleModalParameters {
    involvedVehicle: InvolvedVehicle;
    setInvolvedVehicle: (value: InvolvedVehicle) => void;
    isModalVisible: boolean;
    setModalVisible: (value: boolean) => void;
    onSubmit: () => void;
}

export default function InvolvedVehicleModal({ involvedVehicle, setInvolvedVehicle, isModalVisible, setModalVisible, onSubmit }: InvolvedVehicleModalParameters) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [vehicleRelations, setVehicleRelations] = useState<DropdownResource[]>([]);

    async function getVehicleRelations(): Promise<void> {
        try {
            const response = await appAgentService.getSimplifiedVehicleRelations();
            setVehicleRelations(response);

        } catch (err) {
            console.error(err);
        }
    }

    function isValidForm() {
        if (involvedVehicle.plate === '' || isPlateInvalid(involvedVehicle.plate)) {
            return false;
        }
        if (involvedVehicle.brand === '') {
            return false;
        }
        if (involvedVehicle.model === '') {
            return false;
        }
        if (involvedVehicle.color === '') {
            return false;
        }
        if (involvedVehicle.chassis === '') {
            return false;
        }
        if (involvedVehicle.vehicleRelationId === 0) {
            return false;
        }
        return true;
    }

    useEffect(() => {
        getVehicleRelations();
    }, []);

    return (
        <BasicModal
            isModalVisible={isModalVisible}
            setModalVisible={setModalVisible}
            title={translate('involvedVehicle')}
            width='extra-large'
            primaryButton={{
                text: translate('save'),
                onClick: onSubmit,
                disabled: !isValidForm(),
            }}
        >

            <View style={{ rowGap: 20 }}>
                <View style={styles.row}>
                    <FormInput
                        label={translate('vehicle_metadata_plate')}
                        placeholder={translate('vehicle_metadata_plate')}
                        value={involvedVehicle.plate}
                        onChangeText={(value) => setInvolvedVehicle({ ...involvedVehicle, plate: value.toUpperCase() })}
                        invalid={isPlateInvalid}
                        viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }}
                        required
                    />
                    <FormInput
                        label={translate('brand')}
                        placeholder={translate('brand')}
                        value={involvedVehicle.brand}
                        onChangeText={(value) => setInvolvedVehicle({ ...involvedVehicle, brand: value })}
                        invalid={() => false}
                        viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }}
                        required
                    />
                    <FormInput
                        label={translate('model')}
                        placeholder={translate('model')}
                        value={involvedVehicle.model}
                        onChangeText={(value) => setInvolvedVehicle({ ...involvedVehicle, model: value })}
                        invalid={() => false}
                        viewStyle={[{ flex: 1 }, windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }]}
                        required
                    />
                </View>
                <View style={[styles.row, { zIndex: 2 }]}>
                    <FormInput
                        label={translate('color')}
                        placeholder={translate('color')}
                        value={involvedVehicle.color}
                        onChangeText={(value) => setInvolvedVehicle({ ...involvedVehicle, color: value })}
                        invalid={() => false}
                        viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }}
                        required
                    />
                    <FormInput
                        label={translate('chassis')}
                        placeholder={translate('chassis')}
                        value={involvedVehicle.chassis}
                        onChangeText={(value) => setInvolvedVehicle({ ...involvedVehicle, chassis: value })}
                        invalid={() => false}
                        viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }}
                        required
                    />
                    <View style={[{ gap: 5, zIndex: 2, flex: 1 }, windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%', columnGap: 0 }]}>
                        <FilterDropdown
                            zIndex={2}
                            label={translate('relation')}
                            items={vehicleRelations.map((item) => {
                                return {
                                    label: item.name,
                                    value: item.id
                                };
                            })}
                            value={involvedVehicle.vehicleRelationId}
                            setValue={(value) => {
                                const vehicleRelationId = value(involvedVehicle.vehicleRelationId);
                                setInvolvedVehicle({
                                    ...involvedVehicle,
                                    vehicleRelationId: vehicleRelationId,
                                    vehicleRelation: vehicleRelations.find(vehicleRelation => vehicleRelation.id == vehicleRelationId)
                                });
                            }}
                            searchable={false}
                            required
                        />
                    </View>
                </View>
                <View style={styles.row}>
                    <View style={[{ gap: 5 }, windowInfo.isMobileOrTablet ? { width: '100%' } : {}]}>
                        <MyAppText>{translate('theft')}</MyAppText>
                        <Switch
                            trackColor={{ false: '#767577', true: '#81b0ff' }}
                            thumbColor={involvedVehicle.stolen ? '#f5dd4b' : '#f4f3f4'}
                            onValueChange={(value) => {
                                setInvolvedVehicle({ ...involvedVehicle, stolen: value });
                            }}
                            value={involvedVehicle.stolen}
                        />
                    </View>
                    <FormInput
                        label={translate('restriction')}
                        placeholder={translate('restriction')}
                        value={involvedVehicle.restriction || ''}
                        onChangeText={(value) => setInvolvedVehicle({ ...involvedVehicle, restriction: value })}
                        invalid={() => false}
                        viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { flex: 1 }}
                    />
                </View>
            </View>
        </BasicModal>
    );
}

const styleSheet = createStyleSheet(() => ({
    row: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        columnGap: 30,
        minHeight: 'auto',
        rowGap: 20,
    }
}));
